import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    name: "Marcus T.",
    company: "SunPath Solar — Newark, NJ",
    avatar: "MT",
    avatarBg: "bg-emerald-600",
    score: 5,
    leadsClosed: 11,
    roi: "4.2x",
    quote:
      "I was skeptical at first — I've been burned by shared lead services before. But every lead I've bought here has been mine alone. Closed 11 out of 28 purchased leads in my first 3 months. The pipeline tracker keeps me organized in a way my old spreadsheet never could.",
    highlight: "11 of 28 leads closed",
    tag: "NJ Installer",
  },
  {
    name: "Diana R.",
    company: "Brightfield Energy — Philadelphia, PA",
    avatar: "DR",
    avatarBg: "bg-teal-600",
    score: 5,
    leadsClosed: 7,
    roi: "3.8x",
    quote:
      "The dual scoring system is a game changer. I only buy leads with a Solar Score above 80 and a Lead Score above 75 — my close rate jumped from 18% to 31% just by being selective. The 5-minute reservation is a little stressful but it keeps the marketplace fair.",
    highlight: "Close rate jumped to 31%",
    tag: "PA Installer",
  },
  {
    name: "Kevin O.",
    company: "Apex Solar Group — Hartford, CT",
    avatar: "KO",
    avatarBg: "bg-green-700",
    score: 5,
    leadsClosed: 19,
    roi: "5.1x",
    quote:
      "We've been using this platform for 6 months and it's become our primary lead source. The average bill range on the leads we buy is $280–$350/month — these are real solar candidates, not tire kickers. ROI is consistently above 5x when you factor in system size.",
    highlight: "5.1x ROI over 6 months",
    tag: "CT Installer",
  },
  {
    name: "Priya S.",
    company: "SolarNest — Brooklyn, NY",
    avatar: "PS",
    avatarBg: "bg-lime-700",
    score: 4,
    leadsClosed: 5,
    roi: "2.9x",
    quote:
      "I'm a smaller operation — just me and two reps — so I was worried about volume. But being able to set my ZIP codes and minimum score means I only see leads I can actually handle. Bought 18 leads in 2 months, closed 5. For my size, that's solid.",
    highlight: "5 closes as a solo operator",
    tag: "NY Installer",
  },
  {
    name: "James W.",
    company: "Clearview Solar — Baltimore, MD",
    avatar: "JW",
    avatarBg: "bg-emerald-800",
    score: 5,
    leadsClosed: 14,
    roi: "4.7x",
    quote:
      "What I appreciate most is the transparency. I can see exactly what I spent, what I closed, and what my average cost per acquisition is. No mystery fees, no inflated lead counts. The billing dashboard is cleaner than most CRMs I've paid for.",
    highlight: "14 closes, full billing transparency",
    tag: "MD Installer",
  },
  {
    name: "Tanya B.",
    company: "Horizon Solar — Providence, RI",
    avatar: "TB",
    avatarBg: "bg-green-600",
    score: 5,
    leadsClosed: 8,
    roi: "3.5x",
    quote:
      "The contact info that unlocks after purchase is complete — name, phone, address, everything. I've had leads from other platforms where the phone number was wrong half the time. Here, every lead I've called has picked up or called back within 24 hours.",
    highlight: "100% valid contact info",
    tag: "RI Installer",
  },
];

function StarRating({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-star-fill text-sm ${s <= score ? "text-amber-400" : "text-gray-200"}`}></i>
        </div>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            <i className="ri-star-fill text-amber-500"></i>
            Installer Results
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            Real installers. Real numbers.
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Here's what active installers on the platform are actually seeing — in their own words.
          </p>
          {/* Aggregate rating */}
          <div className="inline-flex items-center gap-3 mt-6 bg-gray-50 border border-gray-100 rounded-full px-5 py-2.5">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((s) => (
                <div key={s} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                </div>
              ))}
            </div>
            <span className="text-gray-900 font-bold text-sm">4.9 / 5</span>
            <span className="text-gray-400 text-sm">from 47 active installers</span>
          </div>
        </div>

        {/* Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`bg-white border rounded-2xl p-6 flex flex-col gap-4 cursor-pointer transition-all duration-300 ${
                activeIdx === i
                  ? "border-green-300 ring-2 ring-green-100"
                  : "border-gray-100 hover:border-gray-200"
              } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: `${i * 80}ms`, transitionProperty: "opacity, transform, border-color, box-shadow" }}
              onClick={() => setActiveIdx(activeIdx === i ? null : i)}
            >
              {/* Top row */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${t.avatarBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{t.avatar}</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm leading-tight">{t.name}</p>
                    <p className="text-gray-400 text-xs leading-tight mt-0.5">{t.company}</p>
                  </div>
                </div>
                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                  {t.tag}
                </span>
              </div>

              {/* Star rating */}
              <StarRating score={t.score} />

              {/* Quote */}
              <blockquote className="text-gray-600 text-sm leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-50">
                <div className="text-center">
                  <p className="text-lg font-black text-gray-900">{t.leadsClosed}</p>
                  <p className="text-xs text-gray-400 leading-tight">Leads Closed</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="text-lg font-black text-green-600">{t.roi}</p>
                  <p className="text-xs text-gray-400 leading-tight">ROI</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
                  </div>
                  <p className="text-xs text-gray-500 font-medium leading-tight mt-0.5">{t.highlight}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-400 text-sm mb-4">Join 47 installers already buying leads on the platform</p>
          <a
            href="/signup"
            className="inline-flex items-center gap-2 bg-black text-white font-bold px-8 py-4 rounded-full text-base hover:bg-gray-900 transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap"
          >
            Start Buying Leads
            <i className="ri-arrow-right-line text-lg"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
